import type { Taster } from '../types'
import { TASTER_NAMES, shuffleArray } from '../utils/devData'

type DevModeDialogProps = {
  isOpen: boolean
  onClose: () => void
  wineCount: number
  existingTasters: Taster[]
  onSetTasters: (tasters: Taster[]) => void
}

function generateRandomScores(wineCount: number): number[] {
  const scores = Array.from({ length: wineCount }, (_, i) => i + 1)
  return shuffleArray(scores)
}

function generateUniqueName(baseName: string, existingNames: Set<string>): string {
  if (!existingNames.has(baseName)) {
    return baseName
  }
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  let name: string
  do {
    const randomLetter = letters[Math.floor(Math.random() * letters.length)]
    name = `${baseName} ${randomLetter}`
  } while (existingNames.has(name))
  return name
}

export function DevModeDialog({
  isOpen,
  onClose,
  wineCount,
  existingTasters,
  onSetTasters,
}: DevModeDialogProps) {
  if (!isOpen) return null

  const handleMockData = () => {
    const shuffledNames = shuffleArray(TASTER_NAMES)
    const usedNames = new Set<string>()
    const tasterCount = Math.floor(Math.random() * 5) + 10 // 10-14 tasters

    const newTasters: Taster[] = []
    for (let i = 0; i < tasterCount; i++) {
      const baseName = shuffledNames[i % shuffledNames.length]
      const name = generateUniqueName(baseName, usedNames)
      usedNames.add(name)

      newTasters.push({
        _key: crypto.randomUUID(),
        name,
        scores: generateRandomScores(wineCount),
      })
    }

    onSetTasters(newTasters)
    onClose()
  }

  const handleMockScores = () => {
    const updatedTasters = existingTasters.map((taster) => ({
      ...taster,
      scores: generateRandomScores(wineCount),
    }))
    onSetTasters(updatedTasters)
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-cream rounded-lg shadow-xl w-full max-w-sm">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4">Developer Mode</h2>

          <div className="space-y-3">
            <button
              onClick={handleMockData}
              className="w-full px-4 py-3 bg-black text-cream rounded hover:bg-black/80 text-left"
            >
              <div className="font-medium">Mock data</div>
              <div className="text-sm text-cream/70">Generate random tasters and scores</div>
            </button>

            <button
              onClick={handleMockScores}
              disabled={existingTasters.length === 0}
              className="w-full px-4 py-3 bg-black text-cream rounded hover:bg-black/80 text-left disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="font-medium">Mock scores</div>
              <div className="text-sm text-cream/70">Generate random scores for existing tasters</div>
            </button>
          </div>

          <div className="mt-6 pt-4 border-t border-black/10 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-black/10 rounded hover:bg-black/20"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
